import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  FileText,
  DollarSign,
  Users,
  Zap,
  Package,
  RefreshCw,
  ArrowRight,
  Plus,
  CreditCard,
  Activity,
} from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api from "@/services/api";
import { generateUUID } from "@/utils/transform";

// Category icons
const CATEGORY_ICONS = {
  plan: Package,
  team_seats: Users,
  credits: CreditCard,
  overage: Activity,
  addon: Zap,
  storage: FileText,
  api: Zap,
};

// Format a Date as YYYY-MM-DD from its LOCAL calendar fields.
//
// Not toISOString(): these Dates are local midnight, and toISOString()
// converts to UTC first, so east-of-UTC browsers get the previous day. In
// Sydney that shifted the whole default window a day early: 1 Jul was sent
// as "2026-06-30". The DatePicker controls already emit local
// YYYY-MM-DD, so this keeps the default consistent with user-picked dates.
const toLocalDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

export const UnbilledUsageSummary = ({
  organisationId,
  organisationName,
  onCreateInvoice,
}) => {
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [generatedItems, setGeneratedItems] = useState([]);

  // Default to current month, formatted in local time; see toLocalDate.
  const defaultPeriod = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: toLocalDate(start),
      end: toLocalDate(end),
    };
  }, []);

  const [period, setPeriod] = useState(defaultPeriod);

  // Fetch unbilled usage summary
  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["admin", "unbilled-usage", organisationId, period],
    queryFn: async () => {
      const params = new URLSearchParams({
        periodStart: period.start,
        periodEnd: period.end,
      });
      const response = await api.get(
        `/admin/organisations/${organisationId}/unbilled-usage?${params}`,
      );
      return response.data?.data?.summary || [];
    },
    enabled: !!organisationId,
  });

  // Fetch full usage data
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ["admin", "organisation-usage", organisationId, period],
    queryFn: async () => {
      const params = new URLSearchParams({
        periodStart: period.start,
        periodEnd: period.end,
      });
      const response = await api.get(
        `/admin/organisations/${organisationId}/usage?${params}`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Generate invoice items mutation
  const generateItemsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/admin/organisations/${organisationId}/generate-items`,
        {
          periodStart: period.start,
          periodEnd: period.end,
        },
      );
      return response.data?.data;
    },
    onSuccess: (data) => {
      setGeneratedItems(data.items || []);
      setSelectedItems(data.items?.map((_, i) => i) || []);
      setShowGenerateDialog(true);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to generate items");
    },
  });

  // Create invoice with selected items
  const handleCreateInvoice = () => {
    const items = selectedItems.map((i) => generatedItems[i]).filter(Boolean);
    if (items.length === 0) {
      toast.error("Please select at least one item");
      return;
    }

    onCreateInvoice?.({
      organisationId,
      items,
      periodStart: period.start,
      periodEnd: period.end,
    });
    setShowGenerateDialog(false);
  };

  // Toggle item selection
  const toggleItem = (index) => {
    setSelectedItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  // Toggle all items
  const toggleAll = () => {
    if (selectedItems.length === generatedItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(generatedItems.map((_, i) => i));
    }
  };

  // Calculate totals
  const selectedTotal = useMemo(
    () =>
      selectedItems.reduce((sum, i) => {
        const item = generatedItems[i];
        return sum + (item?.quantity || 0) * (item?.unitPrice || 0);
      }, 0),
    [selectedItems, generatedItems],
  );

  // Group summary by category
  const groupedSummary = useMemo(() => {
    if (!summaryData) return {};
    return summaryData.reduce((acc, item) => {
      const category = item.event_category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});
  }, [summaryData]);

  // Calculate total unbilled amount
  const totalUnbilled = useMemo(() => {
    if (!summaryData) return 0;
    return summaryData.reduce(
      (sum, item) => sum + parseFloat(item.total_amount || 0),
      0,
    );
  }, [summaryData]);

  const isLoading = summaryLoading || usageLoading;

  return (
    <>
      <Card className="border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Unbilled Usage
              </CardTitle>
              {organisationName && (
                <CardDescription className="mt-1">
                  Usage summary for {organisationName}
                </CardDescription>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchSummary()}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Period Selection */}
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Period Start
              </Label>
              <DatePicker
                value={period.start}
                onChange={(e) =>
                  setPeriod((p) => ({ ...p, start: e.target.value }))
                }
                className="w-40"
              />
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground mb-2" />
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Period End
              </Label>
              <DatePicker
                value={period.end}
                onChange={(e) =>
                  setPeriod((p) => ({ ...p, end: e.target.value }))
                }
                className="w-40"
              />
            </div>
          </div>

          {isLoading && (
            <div className="space-y-3">
              {[...Array(3)].map(() => (
                <Skeleton key={generateUUID()} className="h-16 w-full" />
              ))}
            </div>
          )}

          {!isLoading && Object.keys(groupedSummary).length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No unbilled usage for this period</p>
            </div>
          )}

          {!isLoading && Object.keys(groupedSummary).length > 0 && (
            <>
              {/* Summary Cards */}
              <div className="grid gap-3">
                {Object.entries(groupedSummary).map(([category, items]) => {
                  const Icon = CATEGORY_ICONS[category] || Activity;
                  const categoryTotal = items.reduce(
                    (sum, item) => sum + parseFloat(item.total_amount || 0),
                    0,
                  );
                  const eventCount = items.reduce(
                    (sum, item) => sum + parseInt(item.event_count || 0),
                    0,
                  );

                  return (
                    <div
                      key={category}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-background rounded-md">
                          <Icon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground capitalize">
                            {category.replace("_", " ")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {eventCount} event{eventCount !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground">
                          ${categoryTotal.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="font-medium text-foreground">
                  Total Unbilled
                </span>
                <span className="text-xl font-bold text-foreground">
                  ${totalUnbilled.toFixed(2)}
                </span>
              </div>

              {/* Usage Details */}
              {usageData && (
                <div className="pt-4 border-t border-border space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    Usage Details
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Envelopes</p>
                      <p className="font-semibold text-foreground">
                        {usageData.usage?.envelopesSent || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Team Members</p>
                      <p className="font-semibold text-foreground">
                        {usageData.team?.currentCount || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">SMS Sent</p>
                      <p className="font-semibold text-foreground">
                        {usageData.credits?.sms?.used || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">API Calls</p>
                      <p className="font-semibold text-foreground">
                        {usageData.usage?.apiCalls || 0}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Invoice Button.
                  Labelled explicitly because the manual invoice form has a
                  second, similar-looking action ("Load usage — every event")
                  that bills per event with NO allowance deducted. The two suit
                  different customers and nothing else on screen distinguishes
                  them. */}
              <div className="pt-4">
                <Button
                  onClick={() => generateItemsMutation.mutate()}
                  disabled={generateItemsMutation.isPending}
                  className="w-full"
                >
                  {generateItemsMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Generate items — subscription + overage
                    </>
                  )}
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Builds the invoice from the plan: subscription fee, seats and
                  usage <strong>above</strong> the plan&apos;s included
                  allowance. Use this for customers on a plan.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generated Invoice Items</DialogTitle>
            <DialogDescription>
              Review and select the items to include in the invoice for{" "}
              {organisationName}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {generatedItems.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No billable items found for this period
              </p>
            ) : (
              <>
                <div className="border border-border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={
                              selectedItems.length === generatedItems.length
                            }
                            onCheckedChange={toggleAll}
                          />
                        </TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right w-20">Qty</TableHead>
                        <TableHead className="text-right w-24">Price</TableHead>
                        <TableHead className="text-right w-24">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {generatedItems.map((item, index) => {
                        const total =
                          (item.quantity || 0) * (item.unitPrice || 0);
                        const isSelected = selectedItems.includes(index);

                        return (
                          <TableRow
                            key={generateUUID()}
                            className={isSelected ? "bg-primary/5" : ""}
                          >
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleItem(index)}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-foreground text-sm">
                                  {item.description}
                                </p>
                                <Badge
                                  variant="outline"
                                  className="text-xs mt-1"
                                >
                                  {item.category}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {item.quantity}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              ${item.unitPrice?.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-medium">
                              ${total.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex items-center justify-between mt-4 p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.length} of {generatedItems.length} items
                    selected
                  </span>
                  <span className="font-semibold text-foreground">
                    Total: ${selectedTotal.toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowGenerateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateInvoice}
              disabled={selectedItems.length === 0}
            >
              <FileText className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UnbilledUsageSummary;
